import { API } from "@/api"
import { HttpAPIError } from "@/api/model"
import {
  BizError,
  BizResult,
  Ok,
  RemoteDeskInfo,
  User,
  userDisplayName,
} from "@/models/biz_model"
import {
  DEFAULT_RELAY_PORT,
  DEFAULT_RELAY_PWD,
  DEFAULT_RELAY_USER,
  ERR_AGENT_REBOOT,
  ERR_CONN_TIMEOUT,
  ERR_KICK_OUT,
  ERR_NOT_LOGIN,
  ERR_NO_MEDIA_STREAMS,
  ERR_POWER_OFF,
  ERR_RTC_ERROR,
  ERR_SIGNALING_AUTH,
} from "@/models/constants"
import {
  CONNECT_STATE_CONN_CONNECTED,
  CONNECT_STATE_CONN_DISCONNECTED,
  CONNECT_STATE_CONN_FAILED,
  CONNECT_STATE_MAX_RETRY,
  CONNECT_STATE_PEER_CANCEL,
  CONNECT_STATE_PEER_CLOSE,
  CONNECT_STATE_PEER_NODISPLAY,
  CONNECT_STATE_PEER_NOT_FOUND,
  CONNECT_STATE_PEER_OFFLINE,
  CONNECT_STATE_PWD_VERIFY_FAIL,
  CONNECT_STATE_RECONN,
  CONNECT_STATE_SIGNALING_EXPIRED,
  ConnUserInfo,
  KCSDK,
  PeerConnConfig,
} from "@/sdk"
import { MediaStreams } from "@/sdk/webrtc"

import { AudioInputCache, VideoInputCache, AuthCache, UserCache } from "@/utils/storage"
import { assert } from "@/utils/tools"
import { ClipboardUsecase } from "./clipboard"
import { DataReportUsecase } from "./data_report"
import { DeviceUseCase } from "./device"
import { DisplayUseCase } from "./display"
import { UserUsecase } from "./user"
import { VideoStatsUseCase } from "./video_statistic"
import { store } from "@/store"

type AgentVersionListener = (isNewAgent: boolean) => void
type KeyboardVersionListener = (keyboardVersion?: string) => void
type GameCloudComputerListener = (isGameCloudComputer: boolean) => void
// 获取 Agent version
let agentVersionListener: AgentVersionListener | null = null
let keyboardVersionListener: KeyboardVersionListener | null = null
let _isNewAgent = false
let _keyboardVersion: string | undefined
const gameCloudComputerListeners = new Set<GameCloudComputerListener>()
let _isGameCloudComputer = false

// 添加浏览器检测功能
function getBrowserInfo(): {
  name: string
  version: string
  isSafari: boolean
  isWebKitBased: boolean
} {
  const userAgent = navigator.userAgent
  const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent)
  const isChrome = /chrome/i.test(userAgent)
  const isWebKitBased = /webkit/i.test(userAgent)

  let name = "Unknown"
  let version = "Unknown"

  if (isSafari) {
    name = "Safari"
    const safariVersion = userAgent.match(/version\/(\d+(\.\d+)?)/i)
    version = safariVersion ? safariVersion[1] : "Unknown"
  } else if (isChrome) {
    name = "Chrome"
    const chromeVersion = userAgent.match(/chrome\/(\d+(\.\d+)?)/i)
    version = chromeVersion ? chromeVersion[1] : "Unknown"
  }

  return { name, version, isSafari, isWebKitBased }
}

// 添加 Safari WebRTC 兼容性检查
function checkWebRTCSupport(): { supported: boolean; issues: string[] } {
  const issues: string[] = []

  if (!window.RTCPeerConnection) {
    issues.push("RTCPeerConnection not supported")
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    issues.push("getUserMedia not supported")
  }

  // Safari 特殊检查
  const browserInfo = getBrowserInfo()
  if (browserInfo.isSafari) {
    // Safari 14+ 才完全支持 WebRTC
    const majorVersion = parseInt(browserInfo.version.split(".")[0])
    if (majorVersion < 14) {
      issues.push(
        `Safari version ${browserInfo.version} may have WebRTC compatibility issues`
      )
    }

    // 检查是否在安全上下文中
    if (location.protocol !== "https:" && location.hostname !== "localhost") {
      issues.push("Safari requires HTTPS for WebRTC")
    }
  }

  return {
    supported: issues.length === 0,
    issues,
  }
}

interface OnConnectionCallback {
  onConnected?: () => void
  onDisconnect?: (errCode?: number) => void
  onReconnect?: () => void
}

interface OnAgentMicStateCallback {
  onMicStateChange?: (isOpen: boolean) => void
}

interface OnAgentCameraStateCallback {
  onCameraStateChange?: (isOpen: boolean) => void
}

// Agent 通知客户端：云电脑禁止安装安全/虚拟化软件（对齐 android _onBlockAppInstallation）
interface OnBlockAppInstallCallback {
  onBlockAppInstallation?: () => void
}

// 连接状态
enum ConnState {
  connecting,
  connected,
  // 未连接
  idle,
}

/** 云桌面退出原因，用于错误分类和资源清理。 */
export enum ExitReason {
  screenOff = "screenOff",
  screenLock = "screenLock",
  userExit = "userExit",
  kickedOut = "kickedOut",
  connectionError = "connectionError",
  // 对齐 android ExitReason.forceQuite（android 原拼写为 forceQuite）
  forceQuite = "forceQuite",
  remotePowerOff = "remotePowerOff",
  remoteReboot = "remoteReboot",
  rebootAndReconnect = "rebootAndReconnect",
  abortConnecting = "abortConnecting",
}

// 退出原因对应的上报说明，对齐 android ExitReason.reportMsg。
const EXIT_REASON_MSG: Record<ExitReason, string> = {
  [ExitReason.screenOff]: "息屏退出云电脑",
  [ExitReason.screenLock]: "锁屏退出云电脑",
  [ExitReason.userExit]: "用户主动退出云电脑",
  [ExitReason.kickedOut]: "被其他登录设备踢出",
  [ExitReason.connectionError]: "云电脑连接失败",
  [ExitReason.forceQuite]: "强制退出云电脑",
  [ExitReason.remotePowerOff]: "云电脑关机",
  [ExitReason.remoteReboot]: "云电脑重启",
  [ExitReason.rebootAndReconnect]: "重启云电脑后重新连接",
  [ExitReason.abortConnecting]: "取消正在进行的云电脑连接",
}

/**
 * 云桌面连接状态变化时的回调
 */
const onConnCallbacks = new Set<OnConnectionCallback>()
const onAgentMicStateCallbacks = new Set<OnAgentMicStateCallback>()
const onAgentCameraStateCallbacks = new Set<OnAgentCameraStateCallback>()
const onBlockAppInstallCallbacks = new Set<OnBlockAppInstallCallback>()
let agentMicState: boolean | null = null
let agentCameraState: boolean | null = null

// 远程协助事件回调（对齐 android _onAssistRequest/_onAssistAborted/_onAssistConnected）
// 独立于 onConnCallbacks：assist 在 WebRTC 断开后仍需工作，生命周期跟随信令连接。
export interface OnAssistCallback {
  // 收到管理后台发起的协助请求（status: started）
  onAssistRequest?: (srcDevId: string | null) => void
  // 管理后台中断了远程协助（status: aborted）
  onAssistAborted?: () => void
  // 售后成功连接到云电脑（status: connected）
  onAssistConnected?: () => void
}
const onAssistCallbacks = new Set<OnAssistCallback>()

// assist 回调是否已注册到 KCSDK（仅注册一次，常驻）
let assistCallbackRegistered = false

// 连接状态
let connectState: ConnState = ConnState.idle
let connectAttemptGeneration = 0

// 当前连接的云桌面设备
let curDevice: RemoteDeskInfo | null = null

/**
 * 获取到远端视频流时调用
 *
 * @param stream 远端视频流
 */
let onRemoteMediaStream:
  | ((video: MediaStream, audio: MediaStream | null) => void)
  | null = null

/**
 * 连接信令并注册 assist 回调（仅注册一次，常驻）。
 * 供 connectToSignalingServer（登录后用最新 token）与 connectSignaling（App 启动用缓存 token）复用。
 */
function doConnectSignaling(
  localDevId: string,
  token: string,
  remoteDevId: string
): BizResult<number> {
  KCSDK.connectToSignaling(localDevId, token, remoteDevId)

  // 注册远程协助回调（仅一次，常驻；对齐 android _registerKCSdkCallbacks）
  if (!assistCallbackRegistered) {
    assistCallbackRegistered = true
    KCSDK.setAssistCallback({
      onAssistRequest: (srcDevId) => {
        onAssistCallbacks.forEach((cb) => cb.onAssistRequest?.(srcDevId))
      },
      onAssistAborted: () => {
        onAssistCallbacks.forEach((cb) => cb.onAssistAborted?.())
      },
      onAssistConnected: () => {
        onAssistCallbacks.forEach((cb) => cb.onAssistConnected?.())
      },
    })
  }

  return new Ok(0)
}

/**
 * 连接信令服务器
 * 登录成功后，拿到token，传token 并 连接信令服务器
 * @param remoteDevId 目标云电脑 deviceId，用于 agent_room_id header（确保同 room）
 */
async function connectToSignalingServer(
  remoteDevId: string
): Promise<BizResult<number>> {
  const curAuthHeader = store.getState().auth.auth

  // 获取本地 device id
  const localDevIdRes = await DeviceUseCase.getLocalDeviceId()
  if (localDevIdRes instanceof BizError) {
    return localDevIdRes
  }

  return doConnectSignaling(
    localDevIdRes.unwrap(),
    curAuthHeader.token,
    remoteDevId
  )
}

/**
 * App 启动时用缓存凭证连接常驻信令（不调登录接口），使登录页也能收到远程协助请求。
 * 对齐 android UserUseCase.init -> connectSignaling：仅用缓存的 user/token/device 连接，
 * 缺任一凭证则跳过。登录接口成功后 login_slice 会再用最新 token 重建信令刷新连接。
 */
async function connectSignaling(): Promise<BizResult<number>> {
  if (KCSDK.isSignalingConnected()) {
    return new Ok(0)
  }
  const user = UserCache.getCurrentUser()
  if (!user?.deviceId) {
    // 未登录过或无缓存云电脑，跳过；待登录后由 connectToSignalingServer 建立
    return new Ok(0)
  }
  const auth = AuthCache.getAuth(user.account)
  if (auth === null) {
    return new Ok(0)
  }
  const localDevIdRes = await DeviceUseCase.getLocalDeviceId()
  if (localDevIdRes instanceof BizError) {
    return localDevIdRes
  }
  return doConnectSignaling(localDevIdRes.unwrap(), auth.token, user.deviceId)
}

/**
 * 断开信令服务器（登出/切换用户时调用，对齐 android disconnectSignaling）
 */
async function disconnectSignaling(): Promise<BizResult<number>> {
  KCSDK.closeSignalingConnection()
  return new Ok(0)
}

/**
 * 重连信令服务器（切换用户时调用，对齐 android reconnectSignaling）
 * @param remoteDevId 目标云电脑 deviceId
 */
async function reconnectSignaling(
  remoteDevId: string
): Promise<BizResult<number>> {
  await disconnectSignaling()
  return await connectToSignalingServer(remoteDevId)
}

/**
 * 断开信令服务器
 */
async function closeSignalingServerConn(): Promise<BizResult<number>> {
  KCSDK.closeSignalingConnection()
  return new Ok(0)
}

// 订阅远程协助事件（UI 层调用，对齐 android NotificationManager 弹窗触发）
function addAssistCallback(cb: OnAssistCallback): void {
  onAssistCallbacks.add(cb)
}

// 退订远程协助事件
function removeAssistCallback(cb: OnAssistCallback): void {
  onAssistCallbacks.delete(cb)
}

// 回复远程协助请求（对齐 android KCSdk.sendAssistReply）
function sendAssistReply(targetDevId: string, accepted: boolean): void {
  KCSDK.sendAssistReply(targetDevId, accepted)
}

/**
 * (登录成功后)连接到云桌面
 *
 * @param device 云桌面设备信息
 * @param user 当前登录的用户
 */
async function connectToRemoteDesk(
  device: RemoteDeskInfo,
  user: User | null = null
): Promise<BizResult<number>> {
  // 检查 WebRTC 支持
  const webrtcCheck = checkWebRTCSupport()
  if (!webrtcCheck.supported) {
    const errorMsg = `WebRTC 不支持: ${webrtcCheck.issues.join(", ")}`
    console.error(errorMsg)
    DataReportUsecase.reportError(ERR_RTC_ERROR, errorMsg)
    return new BizError(ERR_RTC_ERROR)
  }

  DataReportUsecase.setDeviceInfo(device)

  if (connectState === ConnState.connecting) {
    // 上一次登录请求可能仍在等待首帧；新登录开始前先回收它，
    // 避免旧请求占用连接状态导致本次登录直接失败。
    // console.warn("Abort stale remote desk connection before reconnect")
    exitRemoteDesk(ExitReason.abortConnecting)
  }
  assert(
    connectState === ConnState.idle,
    `Cannot connect to remote desk, invalid connect state: ${connectState}`
  )

  user = user ?? UserUsecase.getCurrentUser()
  if (user === null) {
    return new BizError(ERR_NOT_LOGIN)
  }

  // 在任何异步操作前占用 connecting 状态，避免自动登录与手动登录并发
  // 穿过状态检查，重复创建云桌面连接。
  connectState = ConnState.connecting
  const attemptGeneration = ++connectAttemptGeneration

  const devLocalIdRes = await DeviceUseCase.getLocalDeviceId()

  if (devLocalIdRes instanceof BizError) {
    if (attemptGeneration === connectAttemptGeneration) {
      connectState = ConnState.idle
    }
    return devLocalIdRes
  }
  if (attemptGeneration !== connectAttemptGeneration) {
    return new BizError(ERR_RTC_ERROR)
  }

  const localDevId = parseInt(devLocalIdRes.unwrap())
  const tokenRes = await API.getDeviceConnToken(localDevId, device.id)
  if (tokenRes instanceof HttpAPIError) {
    if (attemptGeneration === connectAttemptGeneration) {
      connectState = ConnState.idle
    }
    DataReportUsecase.reportError(tokenRes.code, tokenRes.msg ?? "")
    console.error("Get device connection token error: ", tokenRes)
    return BizError.fromHttpError(tokenRes)
  }
  if (attemptGeneration !== connectAttemptGeneration) {
    return new BizError(ERR_RTC_ERROR)
  }
  const token = tokenRes

  // 处理SDK回调，维持连接
  const connectRes = createConnection(device, attemptGeneration).then((res) => {
    if (attemptGeneration !== connectAttemptGeneration) return res
    if (res instanceof Ok) {
      // 连接成功
      connectState = ConnState.connected
    } else {
      // 连接失败
      connectState = ConnState.idle
    }
    return res
  })

  // 开始连接云桌面
  const config = createConnConfig(device, token)
  const connUserInfo: ConnUserInfo = {
    userName: user.account,
    nickName: userDisplayName(user),
    deviceId: device.id.toString(),
    desktopId: device.desktopId,
  }
  const connId = KCSDK.connectToPeer(config, connUserInfo)
  DataReportUsecase.setConnectionId(connId)

  return await connectRes
}

function createConnConfig(
  device: RemoteDeskInfo,
  token: string
): PeerConnConfig {
  const browserInfo = getBrowserInfo()
  const hasGatewayIP = device.gateWayIp !== null && device.gateWayIp.length > 0
  const hasPeerInnerIP =
    device.serverPrivateIp !== null && device.serverPrivateIp.length > 0

  // Safari 特殊的 ICE 服务器配置
  const createSafariOptimizedConfig = () => {
    if (!hasGatewayIP) {
      return {
        token,
        relaySever: null,
        remotePeerInnerIp: null,
        forceRelay: true, // Safari 强制使用中继
        preferIp: null,
      }
    }

    // Safari 优化：使用更简单的 TURN 配置
    const urlsList = [`turn:${device.gateWayIp}:${DEFAULT_RELAY_PORT}`]

    const relayServer: RTCIceServer = {
      urls: urlsList,
      username: DEFAULT_RELAY_USER,
      credential: DEFAULT_RELAY_PWD,
    }

    return {
      token,
      relaySever: relayServer,
      remotePeerInnerIp: device.serverPrivateIp,
      forceRelay: true,
      preferIp: device.gateWayIp,
      // Safari 特殊配置
      safariOptimized: true,
    }
  }

  if (browserInfo.isSafari) {
    return createSafariOptimizedConfig() // 使用 Safari 优化配置
  }

  // 其他浏览器的原有逻辑
  if (hasGatewayIP && hasPeerInnerIP) {
    const urlsList = [`turn:${device.gateWayIp}:${DEFAULT_RELAY_PORT}`]

    const relayServer: RTCIceServer = {
      urls: urlsList,
      username: DEFAULT_RELAY_USER,
      credential: DEFAULT_RELAY_PWD,
    }

    return {
      token,
      relaySever: relayServer,
      remotePeerInnerIp: device.serverPrivateIp,
      forceRelay: true,
      preferIp: device.gateWayIp,
    }
  }

  return {
    token,
    relaySever: null,
    remotePeerInnerIp: null,
    forceRelay: false,
    preferIp: null,
  }
}

/**
 *  创建并维护当前云桌面连接的连接，返回连接结果
 */
function createConnection(
  device: RemoteDeskInfo,
  attemptGeneration: number
): Promise<BizResult<number>> {
  let connResResolve: (data: BizResult<number>) => void
  // 云桌面连接结果
  const connectRes = new Promise<BizResult<number>>((resolve) => {
    connResResolve = resolve
  })

  const onConnected = async () => {
    if (attemptGeneration !== connectAttemptGeneration) {
      connResResolve(new BizError(ERR_RTC_ERROR))
      return
    }
    // 确保获取到音视频流
    const mediaStreams = await tryGetMediaStreams()
    if (attemptGeneration !== connectAttemptGeneration) {
      connResResolve(new BizError(ERR_RTC_ERROR))
      return
    }
    if (mediaStreams === null) {
      // 未获取到音视频流
      if (isPeerConnecting()) {
        connResResolve(new BizError(ERR_NO_MEDIA_STREAMS))
      }
      DataReportUsecase.reportError(ERR_NO_MEDIA_STREAMS, "未获取到音视频流")
      exitRemoteDesk(ExitReason.connectionError, ERR_NO_MEDIA_STREAMS)
      return
    }
    const { video, audio } = mediaStreams
    assert(video !== undefined, "Invalid video stream")
    onRemoteMediaStream?.(video, audio ?? null)

    if (isPeerConnecting()) {
      connResResolve(new Ok(0))
      curDevice = device
      DataReportUsecase.setDeviceInfo(device)
    }

    // 通知订阅者连接成功
    onConnCallbacks.forEach((callback) => {
      callback.onConnected?.()
    })
  }

  const onReconnect = () => {
    if (attemptGeneration !== connectAttemptGeneration) return
    onConnCallbacks.forEach((callback) => {
      callback.onReconnect?.()
    })
  }

  const onDisconnect = (state: number, errCode?: number) => {
    if (attemptGeneration !== connectAttemptGeneration) {
      connResResolve(new BizError(errCode ?? ERR_RTC_ERROR))
      return
    }
    errCode = errCode ?? ERR_RTC_ERROR

    // 特殊处理信令过期的情况
    if (state === CONNECT_STATE_SIGNALING_EXPIRED) {
      // 信令token过期，需要清理用户状态并跳转到登录页

      errCode = ERR_SIGNALING_AUTH

      // 上报信令token过期错误
      DataReportUsecase.reportError(errCode, "信令token过期，需要重新登录")
    }

    if (state === CONNECT_STATE_MAX_RETRY) {
      errCode = ERR_CONN_TIMEOUT
      // 上报连接超时
      DataReportUsecase.reportError(ERR_CONN_TIMEOUT, "连接超时")
    } else {
      // 上报连接状态异常
      DataReportUsecase.reportError(errCode, `RTC连接异常 (${state})`)
    }

    if (isPeerConnecting()) {
      // 返回连接失败
      connResResolve(new BizError(errCode))
    }

    console.error(
      `DeviceUseCase Exit remote desk cause of connection state: ${state}, reason: ${errCode}`
    )
    // 云桌面连接断开，关闭连接并释放资源
    // 退出原因按 errCode 分类，对齐 android connection.dart 的 ExitReason 映射：
    // agent 重启/关机/被踢各自对应不同退出原因与 UI 提示。
    let reason: ExitReason
    if (errCode === 401 || errCode === ERR_KICK_OUT) {
      reason = ExitReason.kickedOut
    } else if (errCode === ERR_AGENT_REBOOT) {
      reason = ExitReason.remoteReboot
    } else if (errCode === ERR_POWER_OFF) {
      reason = ExitReason.remotePowerOff
    } else {
      reason = ExitReason.connectionError
    }
    exitRemoteDesk(reason, errCode)
  }

  // 处理SDK回调
  KCSDK.setSDKCallback({
    onConnect: (state, errCode) => {
      // 处理conect事件，根据state判断连接状态
      switch (state) {
        case CONNECT_STATE_CONN_CONNECTED: {
          // 连接成功
          onConnected()
          break
        }
        case CONNECT_STATE_RECONN:
          // 重连
          onReconnect()
          break
        case CONNECT_STATE_PEER_NOT_FOUND: // 云电脑不在线
        case CONNECT_STATE_PEER_NODISPLAY:
        case CONNECT_STATE_PEER_CLOSE: // 被踢下线
        case CONNECT_STATE_PEER_CANCEL:
        case CONNECT_STATE_CONN_DISCONNECTED:
        case CONNECT_STATE_PEER_OFFLINE:
        case CONNECT_STATE_PWD_VERIFY_FAIL:
        case CONNECT_STATE_CONN_FAILED:
        case CONNECT_STATE_MAX_RETRY:
        case CONNECT_STATE_SIGNALING_EXPIRED:
          // 连接失败
          onDisconnect(state, errCode)
          break
      }
    },
    onGetDisplayInfo: DisplayUseCase.onGetDisplayInfo,
    onGetAgentResolution: DisplayUseCase.onGetAgentResolution,
    onGetAgentVersion,
    onGetKeyboardVersion,
    onGetIsGameCloudComputer,
    onMicStateChange: (isOpen) => {
      // console.log(
      //   `【agent麦克风】device_id=${device.id}, 检测状态=${
      //     isOpen ? "open" : "close"
      //   }`
      // )
      agentMicState = isOpen
      onAgentMicStateCallbacks.forEach((callback) => {
        callback.onMicStateChange?.(isOpen)
      })
    },
    onCameraStateChange: (isOpen) => {
      // console.log(
      //   `【agent摄像头】device_id=${device.id}, 检测状态=${
      //     isOpen ? "open" : "close"
      //   }`
      // )
      agentCameraState = isOpen
      onAgentCameraStateCallbacks.forEach((callback) => {
        callback.onCameraStateChange?.(isOpen)
      })
    },
    onBlockAppInstallation: () => {
      onBlockAppInstallCallbacks.forEach((callback) => {
        callback.onBlockAppInstallation?.()
      })
    },
  })

  return connectRes
}

/**
 * 断开云桌面连接
 */
function exitRemoteDesk(
  reasonOrErrCode: ExitReason | number = ExitReason.userExit,
  errCode?: number
): BizResult<number> {
  if (connectState === ConnState.idle) {
    return new Ok(0)
  }

  // 解析退出原因与错误码：reasonOrErrCode 可能是 ExitReason 或纯 errCode(number)
  const reason =
    typeof reasonOrErrCode === "number"
      ? ExitReason.userExit
      : reasonOrErrCode
  if (typeof reasonOrErrCode === "number") errCode = reasonOrErrCode

  // 上报 exit_rtc：业务层退出云电脑连接，对齐 android DataReport.reportLog("exit_rtc")
  const exitExtra: Record<string, unknown> = {
    msg: EXIT_REASON_MSG[reason] ?? "退出云电脑",
    exit_reason: reason,
  }
  if (errCode !== undefined) {
    exitExtra.error_code = errCode
  }
  DataReportUsecase.reportLog("exit_rtc", exitExtra)

  // 停止统计信息
  VideoStatsUseCase.stopGatheringData()

  // 关闭连接
  KCSDK.closePeerConnection()

  // 执行退出回调
  onConnCallbacks.forEach((callback) => {
    callback.onDisconnect?.(errCode)
  })
  onConnCallbacks.clear()
  // 清空SDK回调
  KCSDK.setSDKCallback(null)
  _keyboardVersion = undefined

  if (curDevice) {
    // 标记关闭音频输入
    AudioInputCache.saveAudioInputEnable(curDevice.id, false)
    VideoInputCache.saveVideoInputEnable(curDevice.id, false)
  }

  // 清理资源
  connectState = ConnState.idle
  curDevice = null
  onRemoteMediaStream = null
  _isNewAgent = false
  _isGameCloudComputer = false
  agentMicState = null
  agentCameraState = null
  DisplayUseCase.onDisconnect()
  ClipboardUsecase.onDisconnect()
  UserUsecase.onDisconnect()
  DataReportUsecase.onDisconnect()

  return new Ok(0)
}

// 订阅连接状态变化
function addConnCallback(callback: OnConnectionCallback): void {
  onConnCallbacks.add(callback)
}

// 取消订阅连接状态变化
function removeConnCallback(callback: OnConnectionCallback): void {
  onConnCallbacks.delete(callback)
}

function addAgentMicStateCallback(callback: OnAgentMicStateCallback): void {
  onAgentMicStateCallbacks.add(callback)
  if (agentMicState !== null) callback.onMicStateChange?.(agentMicState)
}

function removeAgentMicStateCallback(callback: OnAgentMicStateCallback): void {
  onAgentMicStateCallbacks.delete(callback)
}

function addAgentCameraStateCallback(
  callback: OnAgentCameraStateCallback
): void {
  onAgentCameraStateCallbacks.add(callback)
  if (agentCameraState !== null) {
    callback.onCameraStateChange?.(agentCameraState)
  }
}

function removeAgentCameraStateCallback(
  callback: OnAgentCameraStateCallback
): void {
  onAgentCameraStateCallbacks.delete(callback)
}

// 订阅云电脑禁止安装安全/虚拟化软件通知（UI 层调用，对齐 android NotificationManager 弹窗触发）
function addBlockAppInstallCallback(callback: OnBlockAppInstallCallback): void {
  onBlockAppInstallCallbacks.add(callback)
}

// 退订云电脑禁止安装安全/虚拟化软件通知
function removeBlockAppInstallCallback(
  callback: OnBlockAppInstallCallback
): void {
  onBlockAppInstallCallbacks.delete(callback)
}

/**
 * 获取远端音视频流
 * @returns 如果拿到音视频流 [video, audio], 如果超时则返回 null
 */
async function tryGetMediaStreams(): Promise<MediaStreams | null> {
  const streams = KCSDK.getMediaStreams() ?? {}
  if (streams.video) {
    return streams
  }

  let count = 1
  let resolveStreams: (streams: MediaStreams | null) => void
  const pollStreams = new Promise<MediaStreams | null>((resolve) => {
    resolveStreams = resolve
  })
  const interval = setInterval(() => {
    if (connectState === ConnState.idle) {
      // 连接断开
      clearInterval(interval)
      resolveStreams(null)
      return
    }

    const streams = KCSDK.getMediaStreams() ?? {}
    if (streams.video) {
      clearInterval(interval)
      resolveStreams(streams)
      return
    }

    count++
    if (count >= 20) {
      clearInterval(interval)
      resolveStreams(null)
    }
  }, 1000)

  return await pollStreams
}

/**
 *  设置远端视频流回调
 */
function setMediaStreamCallback(
  callback: (video: MediaStream, audio: MediaStream | null) => void
): void {
  onRemoteMediaStream = callback
  const { video, audio } = KCSDK.getMediaStreams() ?? {}
  if (video || audio) {
    onRemoteMediaStream(video!, audio!)
  }
}

// 是否正在连接云桌面
function isPeerConnecting(): boolean {
  return connectState === ConnState.connecting
}

// 是否云桌面已连接
function isPeerConnected(): boolean {
  return connectState === ConnState.connected
}

// 获取当前连接的设备信息
function getConnectedDevice(): RemoteDeskInfo | null {
  return curDevice
}

function onGetAgentVersion(version: number): void {
  _isNewAgent = version >= 21
  if (agentVersionListener !== null) {
    agentVersionListener(_isNewAgent)
  }
}

function onGetKeyboardVersion(keyboardVersion?: string): void {
  _keyboardVersion = keyboardVersion
  if (keyboardVersionListener !== null) {
    keyboardVersionListener(_keyboardVersion)
  }
}

function onGetIsGameCloudComputer(isGameCloudComputer: boolean): void {
  _isGameCloudComputer = isGameCloudComputer
  gameCloudComputerListeners.forEach((listener) => {
    listener(_isGameCloudComputer)
  })
}

// 获取是否为新版本的 Agent
function setAgentVersionListener(listener: AgentVersionListener) {
  agentVersionListener = listener
}

function setKeyboardVersionListener(listener: KeyboardVersionListener) {
  keyboardVersionListener = listener
}

function setGameCloudComputerListener(listener: GameCloudComputerListener) {
  gameCloudComputerListeners.add(listener)

  return () => {
    gameCloudComputerListeners.delete(listener)
  }
}

function isNewAgent(): boolean {
  return _isNewAgent
}

function getKeyboardVersion(): string | undefined {
  return _keyboardVersion
}

function isGameCloudComputer(): boolean {
  return _isGameCloudComputer
}

export const ConnectionUseCase = {
  connectToSignalingServer,
  connectSignaling,
  closeSignalingServerConn,
  disconnectSignaling,
  reconnectSignaling,
  addAssistCallback,
  removeAssistCallback,
  sendAssistReply,
  connectToRemoteDesk,
  exitRemoteDesk,
  setMediaStreamCallback,
  addConnCallback,
  removeConnCallback,
  addAgentMicStateCallback,
  removeAgentMicStateCallback,
  addAgentCameraStateCallback,
  removeAgentCameraStateCallback,
  addBlockAppInstallCallback,
  removeBlockAppInstallCallback,
  isPeerConnecting,
  isPeerConnected,
  getConnectedDevice,
  setAgentVersionListener,
  setKeyboardVersionListener,
  setGameCloudComputerListener,
  isNewAgent,
  getKeyboardVersion,
  isGameCloudComputer,
  getBrowserInfo,
  checkWebRTCSupport, // 导出 WebRTC 支持检查
  ExitReason,
}
